//@ts-check
import { mustBeHtmlElement } from "common/dom-utils";
import { debounce } from "lodash";

/**
 * Wraps a single `<section data-address-role="…">` address field-set inside the
 * checkout form. Owns all per-section DOM concerns: reading values, equality
 * checks across two sections, field lookup, visibility, submit-role name
 * rewriting, mirroring, title injection.
 *
 * The Stimulus controller orchestrates three of these (shipping/billing/
 * licensee) but knows nothing about how fields are laid out within a section.
 */
export class AddressComponent {
  constructor(addressEl, onChange) {
    this.element = mustBeHtmlElement(addressEl, "addressEl");
    // DOM role is fixed at construction time (matches the server-rendered
    // `data-address-role`). Submit role may later diverge in fonts mode when
    // the alternate section plays the billing role.
    this.role = this.element.dataset.addressRole;
    this.submitRole = this.role;
    // Title is rendered server-side with a sensible default inside the
    // partial; the Stimulus controller rewrites it via `setTitle` based on
    // the current merge state.
    this.titleEl = this.element.querySelector("[data-role-title]");
    this.onChange = onChange;
    this.addListeners();
  }

  // -------------------- values --------------------

  /** @returns {Record<string, string>} attr => current input value */
  get values() {
    const result = /** @type {Record<string, string>} */ ({});
    this.eachField((el, attr) => {
      result[attr] = el.value;
    });
    return result;
  }

  /**
   * True iff every address attr across this and `other` holds the same
   * (possibly empty) value. Used to pick the initial merged/split state from
   * the server-rendered inputs.
   */
  equals(other) {
    const a = this.values;
    const b = other.values;
    const keys = Object.keys(a).concat(Object.keys(b));
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if ((a[k] || "") !== (b[k] || "")) return false;
    }
    return true;
  }

  // -------------------- title --------------------
  set title(text) {
    if (this.titleEl) this.titleEl.textContent = text;
  }

  get title() {
    return this.titleEl.textContent;
  }

  // -------------------- fields --------------------

  /** Find the input/select/textarea whose name ends with `[<attr>]`. */
  findField(attr) {
    return this.element.querySelector(`[name$="[${attr}]"]`);
  }

  /** Iterate every address field in this section, yielding `(el, attr)`. */
  eachField(cb) {
    this.element.querySelectorAll("input, select, textarea").forEach((el) => {
      const attr = attrFromName(el.name);
      if (attr) cb(el, attr);
    });
  }

  // -------------------- visibility --------------------

  /** Hide/show the whole section via `.hidden`. */
  setVisible(visible) {
    // console.log(`${visible ? "Showing" : "Hiding"} ${this.role} address`);
    // this.element.style.opacity = visible ? 1 : 0.3;
    this.element.classList.toggle("hidden", !visible);
  }

  /**
   * Hide/show only the address inputs while keeping the section title and
   * any chrome (radios, toggles) visible. Lazily wraps `.form-inputs` in a
   * `.<role>-address-fields` div on first call so subsequent toggles are
   * cheap.
   */
  setFieldsVisible(visible) {
    const host = this._fieldsHost();
    if (host) host.classList.toggle("hidden", !visible);
  }

  _fieldsHost() {
    let host = this.element.querySelector(`.${this.role}-address-fields`);
    if (host) return host;
    const inputs = this.element.querySelector(".form-inputs");
    if (!inputs) return null;
    host = document.createElement("div");
    host.className = `${this.role}-address-fields`;
    inputs.parentNode.insertBefore(host, inputs);
    host.appendChild(inputs);
    return host;
  }

  // -------------------- submit role --------------------

  /**
   * Rewrite every field's `name` attribute so this section submits as
   * `targetRole` (e.g. the alternate section submitting as `billing`). No-op
   * when already at `targetRole`.
   */
  setSubmitRole(targetRole) {
    if (this.submitRole === targetRole) return;
    this.eachField((el, attr) => {
      el.name = `order[${targetRole}_address][${attr}]`;
    });
    this.submitRole = targetRole;
  }

  // -------------------- mirror --------------------

  /**
   * Copy every attr value from `source` into this section's inputs. Only
   * writes when the value actually changed so we don't fire spurious
   * `input`/`change` events on equal values.
   */
  copyFrom(source) {
    source.eachField((srcEl, attr) => {
      const tgtEl = this.findField(attr);
      if (tgtEl && tgtEl.value !== srcEl.value) tgtEl.value = srcEl.value;
    });
  }

  /** Mirror a single attr value from `source` into this section. */
  copyAttrFrom(source, attr) {
    const srcEl = source.findField(attr);
    const tgtEl = this.findField(attr);
    if (!srcEl || !tgtEl) return;
    if (tgtEl.value !== srcEl.value) tgtEl.value = srcEl.value;
  }

  // -------------------- title --------------------

  /** Overwrite the server-rendered title text. No-op if the `<h3>` is missing. */
  setTitle(text) {
    if (this.titleEl) this.titleEl.textContent = text;
  }

  setCompanyName(name) {
    const companyField = this.findField("company");
    if (companyField) companyField.value = name;
  }

  // -------------------- DOM --------------------
  get checkboxesContainerElement() {
    return this.element.querySelector(".form-checkboxes");
  }

  addToggle(toggle) {
    toggle.appendTo(this.checkboxesContainerElement);
  }

  // -------------------- Events --------------------

  addListeners() {
    this._handleChange = this.handleChange.bind(this);

    const inputs = this.element.querySelectorAll(".form-inputs");
    inputs.forEach((input) => {
      input.addEventListener("change", this._handleChange);
    });
    inputs.forEach((input) => {
      input.addEventListener("input", this._handleChange);
    });
  }

  removeListeners() {
    const inputs = this.element.querySelectorAll(".form-inputs");
    inputs.forEach((input) => {
      input.removeEventListener("change", this._handleChange);
    });
    inputs.forEach((input) => {
      input.removeEventListener("input", this._handleChange);
    });
  }

  handleChange(event) {
    this.onChange?.({
      addressComponent: this,
      values: this.values,
      event,
      element: event.target,
    });
  }
}

// Extract the address attr (e.g. "company", "country", "vat_id") from a
// nested-attribute name like "order[billing_address][company]". Returns null
// if the name doesn't match.
export function attrFromName(name) {
  const m = /^order\[[a-z_]+_address_attributes\]\[([a-z_]+)\]$/i.exec(
    name || "",
  );
  return m ? m[1] : null;
}
